import { Component, HostBinding, Input, ViewChild } from '@angular/core';
import { MatDatepicker } from '@angular/material/datepicker';
import { FormControl } from '@angular/forms';

/**
 * The admin form datepicker: a read-only `mat-form-field` input opened via the
 * Material calendar toggle, optionally topped by a small label. This block was
 * duplicated across promotion-form (×3), discountvoucher-form and
 * loyalty-rule-form — the component renders it once, centrally.
 *
 * Pass the `FormControl` via `[control]`; validation messages (usually
 * cross-field date checks) stay at the call site.
 */
@Component({
  selector: 'app-date-picker',
  templateUrl: './date-picker.component.html',
  styleUrls: ['./date-picker.component.scss'],
})
export class DatePickerComponent {
  @Input() control!: FormControl;
  /** Optional small label above the field (translate key). */
  @Input() label = '';
  @Input() labelClass = 'col-sm-12 p-0 mb-0';
  @Input() inputId = '';
  /** Placeholder (translate key) shown while the control is empty. Defaults
   *  to the app-wide "Select a date" so every date field communicates its
   *  empty state — pass '' to deliberately render none. */
  @Input() placeholder = 'admin.global.selectDate';
  /** Optional data-testid applied to the input for e2e targeting. */
  @Input() testId: string | null = null;
  /** Hide the field entirely — the caller renders its own trigger and calls
   *  open() programmatically (wish-date / custom-datepicker pattern). */
  @Input() hiddenTrigger = false;
  /**
   * Selectable bounds, forwarded to `mat-datepicker` so out-of-range days are
   * greyed out and unclickable. Without these a caller can only reject a pick
   * after the fact, which reads to the user as a dead control rather than as
   * "that date is not available" — see #11330.
   */
  @Input() min: Date | null = null;
  @Input() max: Date | null = null;

  /** hiddenTrigger mode: the host must vanish from layout too — a block host
   * with zero-size content still forces a line break between the caller's
   * custom trigger text and its toggle icon. */
  @HostBinding('class.date-picker-host--hidden')
  get hiddenHostClass(): boolean {
    return this.hiddenTrigger;
  }

  @ViewChild(MatDatepicker) picker!: MatDatepicker<Date>;

  /** Open the calendar overlay programmatically. */
  open(): void {
    this.picker?.open();
  }
}
